/**
 * ReasoningEffortPicker — the one control for choosing how hard the AI thinks.
 *
 * Effort is a runtime choice, so the same picker appears wherever a model is
 * chosen: per skill in organization AI settings, per lane on an agent's
 * Processing tab, and inline beside the model selector in an AI chat or the
 * Luma panel. One component means the four surfaces offer the same levels, in
 * the same order, under the same names.
 *
 * `allowedLevels` must come from the backend (`reasoning_allowed_levels`),
 * which narrows to the model actually resolved — the `mini` category reasons
 * but gpt-4o-mini does not, and offering a level the model rejects lets someone
 * save a setting that is silently dropped on every call. The order is the
 * config's own cheapest-to-deepest order and is never re-sorted here.
 *
 * Renders NOTHING when `allowedLevels` is empty: there is no choice to make.
 * Surfaces that want to explain the absence (the settings pages say "not
 * supported by this model") render that themselves.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, Gauge } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';

/** Radix Select has no null value, so "inherit" travels as a sentinel. */
const INHERIT_VALUE = '__inherit__';

export type ReasoningEffortVariant = 'default' | 'compact';

export interface ReasoningEffortPickerProps {
  /** Chosen level, or null to inherit */
  value: string | null;
  /** Called with the new level, or null when the user picks "inherit" */
  onChange: (level: string | null) => void;
  /**
   * Levels this provider + model accepts, cheapest first (backend-supplied).
   * NOTE: an empty array makes the whole component render NOTHING — there is no
   * choice to offer. Don't wrap it in a bordered or labelled container that
   * would be left visibly empty; guard the container instead.
   */
  allowedLevels: string[];
  /** The level "inherit" currently resolves to, shown beside the inherit option */
  defaultLevel?: string | null;
  /** What inheriting means here, e.g. "Organization default". Defaults to "Model default". */
  inheritLabel?: string;
  /** 'default' for form fields, 'compact' for the inline chat control */
  variant?: ReasoningEffortVariant;
  /** Optional label above the field (form variant only) */
  label?: string;
  disabled?: boolean;
  className?: string;
  'aria-label'?: string;
}

export function ReasoningEffortPicker({
  value,
  onChange,
  allowedLevels,
  defaultLevel,
  inheritLabel,
  variant = 'default',
  label,
  disabled = false,
  className,
  'aria-label': ariaLabel,
}: ReasoningEffortPickerProps) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);

  if (allowedLevels.length === 0) return null;

  const levelLabel = (level: string) => t(`reasoningEffort.levels.${level}`);
  const inheritText = inheritLabel ?? t('reasoningEffort.modelDefault');
  const inheritDetail = defaultLevel ? levelLabel(defaultLevel) : null;

  // A stored level the model no longer accepts must not silently disappear from
  // the trigger — surface it so the user can see what to correct.
  const isStale = value !== null && !allowedLevels.includes(value);

  // ── Compact variant: inline beside the chat model selector ──
  if (variant === 'compact') {
    return (
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            aria-label={ariaLabel ?? t('reasoningEffort.label')}
            className={cn(
              'inline-flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap',
              'transition-opacity hover:opacity-100 touch:min-h-11',
              disabled ? 'cursor-default opacity-60' : 'cursor-pointer opacity-80',
              className,
            )}
          >
            <Gauge className="h-3.5 w-3.5 shrink-0" />
            <span className={cn(isStale && 'text-warning')}>
              {value ? levelLabel(value) : (inheritDetail ?? inheritText)}
            </span>
            {!disabled && <ChevronDown className="h-3 w-3 shrink-0" />}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" className="w-56">
          <DropdownMenuItem onSelect={() => onChange(null)}>
            <Check className={cn('h-4 w-4 shrink-0', value === null ? 'opacity-100' : 'opacity-0')} />
            <span className="text-muted-foreground">{inheritText}</span>
            {inheritDetail && (
              <span className="ms-1 text-xs text-muted-foreground">({inheritDetail})</span>
            )}
          </DropdownMenuItem>
          {allowedLevels.map((level) => (
            <DropdownMenuItem key={level} onSelect={() => onChange(level)}>
              <Check className={cn('h-4 w-4 shrink-0', value === level ? 'opacity-100' : 'opacity-0')} />
              {levelLabel(level)}
            </DropdownMenuItem>
          ))}
          {isStale && value && (
            <DropdownMenuItem disabled>
              <Check className="h-4 w-4 shrink-0" />
              {t('reasoningEffort.staleLevel', { level: levelLabel(value) })}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // ── Default variant: a form field ──
  return (
    <div className={cn('w-full', className)}>
      {label && <Label className="mb-1.5 block">{label}</Label>}
      <Select
        value={value ?? INHERIT_VALUE}
        onValueChange={(next) => onChange(next === INHERIT_VALUE ? null : next)}
        disabled={disabled}
      >
        <SelectTrigger aria-label={ariaLabel ?? label ?? t('reasoningEffort.label')}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={INHERIT_VALUE}>
            {inheritDetail ? `${inheritText} (${inheritDetail})` : inheritText}
          </SelectItem>
          {allowedLevels.map((level) => (
            <SelectItem key={level} value={level}>
              {levelLabel(level)}
            </SelectItem>
          ))}
          {isStale && value && (
            <SelectItem value={value}>
              {t('reasoningEffort.staleLevel', { level: levelLabel(value) })}
            </SelectItem>
          )}
        </SelectContent>
      </Select>
    </div>
  );
}

/**
 * ReasoningEffortList — read-only display of the levels a model accepts.
 *
 * The AI catalog tracks WHICH MODELS ARE AVAILABLE, so it reports each model's
 * effort range as part of that inventory; it is deliberately not a place to
 * pick one. Rendered as inert text rather than the picker above, because a
 * control that looks selectable and isn't reads as broken.
 */
export function ReasoningEffortList({
  levels,
  className,
}: {
  levels: string[];
  className?: string;
}) {
  const { t } = useTranslation('common');

  return (
    <span className={cn('text-xs text-muted-foreground', className)}>
      {levels.length === 0
        ? t('reasoningEffort.noneSupported')
        : levels.map((level) => t(`reasoningEffort.levels.${level}`)).join(' · ')}
    </span>
  );
}
